/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

// @mui material components
import Divider from '@mui/material/Divider';
import Switch from '@mui/material/Switch';
import IconButton from '@mui/material/IconButton';
import Icon from '@mui/material/Icon';
import { Theme } from '@mui/material/styles';

// Material Dashboard 2 PRO React TS components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';

// Custom styles for the Configurator
import ConfiguratorRoot from 'examples/Configurator/ConfiguratorRoot';

// Material Dashboard 2 PRO React context
import {
    useMaterialUIController,
    setOpenConfigurator,
    setTransparentSidenav,
    setWhiteSidenav,
    setMiniSidenav,
    setSidenavColor,
    setDarkMode,
} from 'context';

function Configurator(): JSX.Element {
    const { t } = useTranslation();
    const [controller, dispatch] = useMaterialUIController();
    const {
        openConfigurator,
        miniSidenav,
        sidenavColor,
        darkMode,
    } = controller;
    const sidenavColors: (
        | 'primary'
        | 'secondary'
        | 'info'
        | 'success'
        | 'warning'
        | 'error'
        | 'light'
        | 'dark'
    )[] = ['dark', 'secondary', 'info', 'success', 'error'];

    const handleCloseConfigurator = () => setOpenConfigurator(dispatch, false);
    const handleWhiteSidenav = () => {
        setWhiteSidenav(dispatch, true);
        setTransparentSidenav(dispatch, false);
    };
    const handleDarkSidenav = () => {
        setWhiteSidenav(dispatch, false);
        setTransparentSidenav(dispatch, false);
    };
    const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);
    const handleDarkMode = () => {
        setDarkMode(dispatch, !darkMode);
    };

    useEffect(() => {
        !darkMode ? handleWhiteSidenav() : handleDarkSidenav();
    }, [darkMode]);

    return (
        <ConfiguratorRoot variant="permanent" ownerState={{ openConfigurator }}>
            <MDBox
                display="flex"
                justifyContent="space-between"
                alignItems="baseline"
                pt={4}
                pb={0.5}
                px={3}
            >
                <MDBox>
                    <MDTypography variant="h5">{t('configuratorScreen.configuracoesPlataforma')}</MDTypography>
                    <MDTypography variant="body2" color="text">
                        mgFlow
                    </MDTypography>
                </MDBox>

                <Icon
                    sx={({ typography: { size }, palette: { white, dark } }) => ({
                        fontSize: `${size.lg} !important`,
                        color: darkMode ? white.main : dark.main,
                        stroke: 'currentColor',
                        strokeWidth: '2px',
                        cursor: 'pointer',
                        transform: 'translateY(5px)',
                    })}
                    onClick={handleCloseConfigurator}
                >
                    close
                </Icon>
            </MDBox>

            <Divider />

            <MDBox pt={0.5} pb={3} px={3}>
                <MDBox>
                    <MDTypography variant="h6">{t('configuratorScreen.cores')}</MDTypography>

                    <MDBox mb={0.5}>
                        {sidenavColors.map((color) => (
                            <IconButton
                                key={color}
                                aria-label={color}
                                sx={({
                                    borders: { borderWidth },
                                    palette: { white, dark, background },
                                    transitions,
                                }: Theme) => ({
                                    width: '24px',
                                    height: '24px',
                                    padding: 0,
                                    border: `${borderWidth[1]} solid ${
                                        darkMode ? background.sidenav : white.main
                                    }`,
                                    borderColor: () => {
                                        let borderColorValue = sidenavColor === color && dark.main;

                                        if (darkMode && sidenavColor === color) {
                                            borderColorValue = white.main;
                                        }

                                        return borderColorValue;
                                    },
                                    transition: transitions.create('border-color', {
                                        easing: transitions.easing.sharp,
                                        duration: transitions.duration.shorter,
                                    }),
                                    backgroundImage: ({
                                        functions: { linearGradient },
                                        palette: { gradients },
                                    }) =>
                                        linearGradient(
                                            gradients[color].main,
                                            gradients[color].state
                                        ),

                                    '&:not(:last-child)': {
                                        mr: 1,
                                    },

                                    '&:hover, &:focus, &:active': {
                                        borderColor: darkMode ? white.main : dark.main,
                                    },
                                })}
                                onClick={() => setSidenavColor(dispatch, color)}
                            />
                        ))}
                    </MDBox>
                </MDBox>
            </MDBox>

            <Divider />
            <MDBox display="flex" justifyContent="space-between" alignItems="center" lineHeight={1}>
                <MDTypography variant="h6">{t('configuratorScreen.barraPequena')}</MDTypography>

                <Switch checked={miniSidenav} onChange={handleMiniSidenav} />
            </MDBox>

            <MDBox
                mt={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                lineHeight={1}
            >
                <MDTypography variant="h6">{t('configuratorScreen.modoEscuro')}</MDTypography>
                <Switch checked={darkMode} onChange={handleDarkMode} />
            </MDBox>
            <Divider />
        </ConfiguratorRoot>
    );
}

export default Configurator;
